import {
  bowlingSummaryDayKey,
  BowlingSummaryDay,
  BowlingSummaryDayMap,
  Workout,
} from '@strength/domain-workout';
import { WorkoutWithCycleAndSession } from '../types';
import { ActualWorkout } from './types';

export type CrossBlockChartWorkouts = {
  workouts: Workout[];
  /**
   * workoutIds of the overlay-chosen workouts that carry a recorded actual.
   * Callers fold these into the chart's hasActual set alongside the current
   * block's own actual layers.
   */
  overlayActualWorkoutIds: string[];
};

/**
 * Reconciles the current block's chart workouts with the cross-block overlay so
 * the chart totals match what the grid renders. The grid resolves each slot
 * through `resolveSlotWorkout`, which shows the overlay's chosen workout in
 * place of the current-block workout on an overlapping day — so the chart must
 * substitute rather than append, otherwise that day's load is counted twice and
 * the acute/chronic ratio doubles.
 *
 * Per athlete day:
 * - `kind: 'current'` days keep the current-block workouts as-is.
 * - Other overlay days replace every current workout on that date with the
 *   single chosen workout (`primary` for duplicates, the draft itself
 *   otherwise). The chart uses `actualWorkout ?? workout` so recorded load
 *   matches ActualSlot / bowling-load-days rather than the prescription.
 * - Overlay days with no current-block workout are appended, so cross-block-only
 *   days still contribute load.
 * - Dates absent from the overlay are left untouched, in their original order.
 */
export const buildCrossBlockChartWorkouts = (
  currentWorkouts: Workout[],
  overlay: BowlingSummaryDayMap,
  profileId: string | null,
): CrossBlockChartWorkouts => {
  if (!profileId || overlay.size === 0) {
    return { workouts: currentWorkouts, overlayActualWorkoutIds: [] };
  }

  const prefix = `${profileId}|`;
  const chosenByDate = new Map<
    string,
    { workout: Workout; hasActual: boolean }
  >();
  for (const [key, day] of overlay) {
    if (day.kind === 'current' || !key.startsWith(prefix)) continue;
    const chosen = day.kind === 'duplicate' ? day.primary : day;
    chosenByDate.set(key.slice(prefix.length), {
      workout: chosen.actualWorkout ?? chosen.workout,
      hasActual: chosen.hasActual,
    });
  }

  if (chosenByDate.size === 0) {
    return { workouts: currentWorkouts, overlayActualWorkoutIds: [] };
  }

  const workouts: Workout[] = [];
  const replacedDates = new Set<string>();
  for (const workout of currentWorkouts) {
    const assignedDate = workout.assignedDate;
    const chosen = assignedDate ? chosenByDate.get(assignedDate) : undefined;
    if (!chosen || !assignedDate) {
      workouts.push(workout);
      continue;
    }
    // Every current workout on this date collapses into the one overlay
    // workout the grid shows, so emit it only for the first one seen.
    if (replacedDates.has(assignedDate)) continue;
    replacedDates.add(assignedDate);
    workouts.push(chosen.workout);
  }

  const overlayActualWorkoutIds: string[] = [];
  for (const [assignedDate, chosen] of chosenByDate) {
    if (!replacedDates.has(assignedDate)) workouts.push(chosen.workout);
    if (chosen.hasActual) {
      overlayActualWorkoutIds.push(chosen.workout.workoutId);
    }
  }

  return { workouts, overlayActualWorkoutIds };
};

/**
 * Walks prescription workout rows and attaches the matching BowlingSummaryDay
 * for the given athlete. Also creates placeholder entries for cross-block-only
 * days — days that appear in the overlay but have no current-block workout —
 * so those cells render the source workout's values rather than staying blank.
 */
export const applyCrossBlockOverlayToPrescriptions = (
  workouts: WorkoutWithCycleAndSession[],
  overlay: BowlingSummaryDayMap,
  profileId: string | null,
): WorkoutWithCycleAndSession[] => {
  if (!profileId || overlay.size === 0) return workouts;
  const workoutDates = new Set(
    workouts.flatMap((w) => (w.assignedDate ? [w.assignedDate] : [])),
  );

  const result = workouts.map((w) => {
    if (!w.assignedDate) return w;
    const day = overlay.get(bowlingSummaryDayKey(profileId, w.assignedDate));
    if (!day) return w;
    return { ...w, crossBlock: day };
  });

  for (const { assignedDate, day } of crossBlockOnlyDays(overlay, profileId, workoutDates)) {
    result.push(makePrescriptionPlaceholder(assignedDate, day));
  }

  return result;
};

/**
 * Walks ActualWorkout rows and attaches the matching BowlingSummaryDay from
 * the cross-block overlay index. Also creates placeholder entries for
 * cross-block-only days so those cells render the source workout's values.
 * Days with no cross-block entry are left as `undefined` so the slot renders
 * the current-block values normally.
 */
export const applyCrossBlockOverlayToActuals = (
  actuals: ActualWorkout[],
  overlay: BowlingSummaryDayMap,
  profileId?: string | null,
): ActualWorkout[] => {
  if (overlay.size === 0) return actuals;
  const actualDates = new Set(actuals.map((a) => a.assignedDate));

  const result = actuals.map((row) => {
    const day = overlay.get(
      bowlingSummaryDayKey(row.context.profileId, row.assignedDate),
    );
    return day ? { ...row, crossBlock: day } : row;
  });

  if (profileId) {
    for (const { assignedDate, day } of crossBlockOnlyDays(overlay, profileId, actualDates)) {
      result.push(makeActualPlaceholder(assignedDate, profileId, day));
    }
  }

  return result;
};

// Returns overlay entries for a given athlete that have no matching date in
// existingDates — these are days that exist only in the cross-block source.
function crossBlockOnlyDays(
  overlay: BowlingSummaryDayMap,
  profileId: string,
  existingDates: Set<string>,
): Array<{ assignedDate: string; day: BowlingSummaryDay }> {
  const prefix = `${profileId}|`;
  const result: Array<{ assignedDate: string; day: BowlingSummaryDay }> = [];
  for (const [key, day] of overlay) {
    if (day.kind === 'current' || !key.startsWith(prefix)) continue;
    const assignedDate = key.slice(prefix.length);
    if (!existingDates.has(assignedDate)) result.push({ assignedDate, day });
  }
  return result;
}

// Synthetic workout row so cross-block-only dates get a slot in the grid.
// The current block has nothing on this date, so sections is empty — the slot
// renders from crossBlock.workout instead via resolveSlotWorkout.
function makePrescriptionPlaceholder(
  assignedDate: string,
  crossBlock: BowlingSummaryDay,
): WorkoutWithCycleAndSession {
  return {
    workoutId: `cross-block-placeholder|${assignedDate}`,
    name: null,
    assignedDate,
    sections: [],
    actual: undefined,
    modifiedPublished: undefined,
    exists: false,
    status: { type: 'planned' },
    session: { id: 0, name: '' },
    cycle: { id: 0, name: '', startDate: assignedDate, endDate: assignedDate },
    isAthleteOnlyLayer: false,
    crossBlock,
  } as WorkoutWithCycleAndSession;
}

// Mirror of makePrescriptionPlaceholder for the actuals grid. Empty context
// fields are safe here — the slot is disabled (isOverlay=true) so no mutation
// is triggered from this row.
function makeActualPlaceholder(
  assignedDate: string,
  profileId: string,
  crossBlock: BowlingSummaryDay,
): ActualWorkout {
  return {
    assignedDate,
    context: {
      blockId: '',
      profileId,
      athleteVersion: 0,
      blockVersion: 0,
      blockLayerId: undefined,
      athleteLayerId: '',
      actualLayerId: '',
      workoutStatus: 'planned',
    },
    workout: {
      workoutId: `cross-block-placeholder|${assignedDate}`,
      name: null,
      assignedDate,
      sections: [],
    },
    hasActual: 'hasActual' in crossBlock ? crossBlock.hasActual : false,
    crossBlock,
  };
}
